import BaseApi from '@core/api/model';

import NotificationTemplateTypeEnum from '@constants/enums/notification/templateType';

import { countryFromAlpha2Code } from '@utils/i18n';

import NotificationPreference from '@/models/NotificationPreference';

export class OrganisationApi extends BaseApi {
    static resource = 'organisations';

    get endpoints() {
        return {
            ...super.endpoints,
            getNotificationPreferencesTemplate: this.getNotificationPreferencesTemplate.bind(this),
            updateNotificationPreferencesTemplate: this.updateNotificationPreferencesTemplate.bind(this)
        };
    }

    _fixGetCompanyInfo(data) {
        if (Array.isArray(data)) {
            return data.map(item => this._fixGetCompanyInfo(item));
        }

        if (!data || typeof data !== 'object') {
            return data;
        }

        // Recurse into nested properties before processing the current object.
        Object.keys(data).forEach(key => {
            data[`${key}`] = this._fixGetCompanyInfo(data[`${key}`]);
        });

        // Apply transformation if company_info is present.
        if ('company_info' in data) {
            if (!data.company_info) {
                data.company_info = {};
            }

            const { company_info } = data;
            if (company_info.tel && !company_info.phone_number) {
                const { tel } = company_info;
                let prefix = tel.prefix || null;

                if (!prefix && tel.region) {
                    const country = countryFromAlpha2Code(tel.region);
                    if (country) {
                        prefix = `+${country.callingCode}`;
                    }
                }

                company_info.phone_number = {
                    number: tel.national_number || null,
                    country: tel.region,
                    calling_code: prefix
                };

                delete company_info.tel;
            }
        }

        return data;
    }

    _filterResponseData(data) {
        const baseData = super._filterResponseData(data);
        return this._fixGetCompanyInfo(baseData);
    }

    _fixSaveCompanyInfo(data) {
        if (Array.isArray(data)) {
            return data.map(item => this._fixSaveCompanyInfo(item));
        }

        if (!data || typeof data !== 'object') {
            return data;
        }

        // Recurse into nested properties before processing the current object.
        Object.keys(data).forEach(key => {
            data[`${key}`] = this._fixSaveCompanyInfo(data[`${key}`]);
        });

        // Apply transformation if company_info is present.
        if (data.company_info && data.company_info.phone_number) {
            const { phone_number } = data.company_info;
            data.company_info.tel = {
                national_number: phone_number.number,
                region: phone_number.country,
                prefix: phone_number.calling_code
            };
            delete data.company_info.phone_number;
        }

        return data;
    }

    _filterSaveData(data) {
        const baseData = super._filterSaveData(data);
        return this._fixSaveCompanyInfo(baseData);
    }

    async getNotificationPreferencesTemplate(id, notificationTemplateType = NotificationTemplateTypeEnum.USERS) {
        if (!id) {
            throw new ReferenceError('Missing ID required while attempting to fetch notification preferences template');
        }

        if (!NotificationTemplateTypeEnum[`${notificationTemplateType}`]) {
            throw new ReferenceError(`Invalid notification template type: ${notificationTemplateType}`);
        }

        let url = `${this.base_url}/${id}/preferences-template`;

        url += `/${NotificationTemplateTypeEnum[`${notificationTemplateType}`]}`;

        const response = await BaseApi.api.get(url);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            response.data = response.data.map(preference => new NotificationPreference(preference));
        });
    }

    async updateNotificationPreferencesTemplate(id, notificationTemplateType, template) {
        if (!id) {
            throw new ReferenceError(
                'Missing ID required while attempting to update notification preferences template'
            );
        }

        if (!NotificationTemplateTypeEnum[`${notificationTemplateType}`]) {
            throw new ReferenceError(`Invalid notification template type: ${notificationTemplateType}`);
        }

        let url = `${this.base_url}/${id}/preferences-template`;

        url += `/${NotificationTemplateTypeEnum[`${notificationTemplateType}`]}`;

        const response = await BaseApi.api.patch(url, template);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            response.data = response.data.map(preference => new NotificationPreference(preference));
        });
    }
}

export default OrganisationApi;
